'use client';

import Link from 'next/link';
import CloudinaryImage from '@/components/ui/CloudinaryImage';
import Icon from '@/components/ui/AppIcon';

interface Article {
  id: string;
  title: string;
  excerpt: string;
  featuredImage: {
    url: string;
    alt: string;
  };
  author: string;
  publishedAt: string;
  category: string;
  categoryColor?: string;
  readTime: number;
  tags: string[];
  isFeatured?: boolean;
  slug: string;
}

interface NewsArticleCardProps {
  article: Article;
  variant: 'standard' | 'featured';
}

const NewsArticleCard = ({ article }: NewsArticleCardProps) => {
  const formattedDate = new Date(article.publishedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="bg-card border border-border rounded-industrial overflow-hidden industrial-shadow hover:shadow-lg transition-industrial">
      <Link href={`/article-detail?slug=${article.slug}`} className="block">
        {/* Featured Image */}
        <div className="relative h-48 overflow-hidden bg-muted">
          <CloudinaryImage
            src={article.featuredImage.url || ''}
            alt={article.featuredImage.alt || article.title}
            fill
            preset="card"
            responsive
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="object-cover"
          />

          {/* Category Badge */}
          <div className="absolute top-3 left-3">
            <span className="bg-primary text-primary-foreground px-3 py-1 rounded text-xs font-heading font-bold uppercase tracking-wide">
              {article.category}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 space-y-3">
          {/* Title */}
          <h3 className="font-heading text-lg font-bold text-foreground hover:text-primary transition-industrial line-clamp-2">
            {article.title}
          </h3>

          {/* Excerpt */}
          <p className="text-text-secondary text-sm line-clamp-3">
            {article.excerpt}
          </p>

          {/* Meta Information */}
          <div className="flex items-center justify-between pt-3 border-t border-border">
            <div className="flex items-center space-x-2">
              <Icon name="UserCircleIcon" size={16} className="text-text-secondary" />
              <span className="text-sm text-text-secondary">{article.author}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="CalendarIcon" size={16} className="text-text-secondary" />
              <time className="text-sm text-text-secondary">{formattedDate}</time>
            </div>
          </div>

          {/* Read More Link */}
          <div className="pt-2">
            <span className="text-primary font-heading font-bold text-sm uppercase tracking-wide flex items-center space-x-1 hover:text-accent transition-industrial">
              <span>Read More</span>
              <Icon name="ArrowRightIcon" size={16} />
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default NewsArticleCard;